import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {Header as TanstackHeader, Row as TanstackRow, Column} from '@tanstack/react-table'
import {Navigate, Route, Routes, Outlet} from 'react-router-dom'
import {HeaderTabs} from './HeaderTabs'
import {KTCard, KTIcon, KTCardBody} from '../../../../_metronic/helpers'
import toast from 'react-hot-toast'
import {useLayout} from '../../../../_metronic/layout/core'
import {AgingReportData, getAgingReport} from '../API/AgingReport'
import {getAgingReportColumns} from './Columns'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,

  // removed unused imports
} from '@tanstack/react-table'

import clsx from 'clsx'
import jsPDF from 'jspdf'
import {Loader} from '../../product-master/loader'

const AgingReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'receivables' | 'payables'>('receivables')

  return (
    <Routes>
      <Route
        element={
          <>
            <HeaderTabs setActiveTab={setActiveTab} />
            <Report activeTab={activeTab} />
            <Outlet />
          </>
        }
      >
        <Route path='receivables' element={<></>} />
        <Route path='payables' element={<></>} />

        <Route index element={<Navigate to='/transactions/aging-report/receivables' />} />
      </Route>
    </Routes>
  )
}

const Report: React.FC<{activeTab: 'receivables' | 'payables'}> = ({activeTab}) => {
  const {company, financialYear, fyEndDate} = useLayout()
  const tableRef = useRef(null)

  const [toDate, setToDate] = useState<string>('')
  const [isDateUpdate, setIsDateUpdate] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<AgingReportData[]>([])
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  useEffect(() => {
    if (fyEndDate) {
      setToDate(fyEndDate)
    } else {
      // Set the initial date range based on financial year
      const storedToDate = localStorage.getItem('toDate')
      if (!storedToDate) {
        if (!financialYear || !financialYear?.value) {
        } else {
          const [, year2] = financialYear.value.split('-')
          setToDate(new Date(Number(year2.trim()), 3, 1).toISOString().split('T')[0])
        }
      } else {
        setToDate(storedToDate)
      }
    }
    setIsDateUpdate(true)
  }, [fyEndDate, financialYear])

  useEffect(() => {
    if (toDate) {
      localStorage.setItem('toDate', toDate)
    }
  }, [toDate])

  const columns = useMemo(() => getAgingReportColumns(activeTab), [activeTab])

  const getData = useCallback(async () => {
    if (
      company &&
      company?.value &&
      financialYear &&
      financialYear?.value &&
      toDate &&
      isDateUpdate
    ) {
      setIsError(false)
      setIsLoading(true)
      getAgingReport(company?.value, financialYear.value, toDate, activeTab)
        .then((res) => {
          res && setData(res)
        })
        .catch((err) => {
          toast.error(
            err.message || 'Something went wrong. Please check the company and FY selected'
          )
          setData([])
          setIsError(true)
        })
        .finally(() => {
          setIsLoading(false)
          setIsDateUpdate(false)
        })
    }
  }, [company, financialYear, toDate, isDateUpdate, activeTab])

  useEffect(() => {
    getData()
  }, [getData])

  // Tanstack Table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enablePinning: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        left: ['parentLedger', 'subLedger'],
        right: ['ledgerId'],
      },
    },
  })

  useEffect(() => {
    setIsDateUpdate(true)
  }, [activeTab])
  return (
    <>
      <KTCard className='mb-5'>
        <div className='card-header border-0'>
          <div className='card-title'>
            {/* begin::Search */}
            <div className='d-flex align-items-center position-relative my-1'>
              <input
                type='date'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14'
                placeholder='Search'
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value)
                }}
              />
              <button
                type='button'
                className='btn btn-lg ms-3'
                style={{
                  borderRadius: '1000%',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  backgroundColor: !isButtonHovered ? '#f0f0f0' : '#b0b0b0',
                }}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                onClick={() => {
                  if (!toDate) {
                    toast.error('Please make sure To date is selected')
                  } else {
                    setIsDateUpdate(true)
                  }
                }}
              >
                <KTIcon iconName='arrows-circle' className='fs-3 px-0' />
              </button>
            </div>
            {/* end::Search */}
          </div>
          {/* begin::Card toolbar */}
          <div className='card-toolbar'>
            {/* begin::Group actions */}

            <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
              {/* begin::Add user */}
              <button
                type='button'
                className='btn p-0'
                disabled={!data.length}
                style={{
                  backgroundColor: 'var(--bs-primary)',
                  color: 'var(--bs-primary-inverse)',
                  display: 'inline-flex',
                  height: '43.59px',
                  alignItems: 'center',
                  justifyContent: 'space-between',

                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                }}
                onClick={() => {
                  toast.success('Download has been started successfully')
                  const doc = new jsPDF({
                    format: 'a2',
                    unit: 'px',
                    hotfixes: ['px_scaling'],
                  })

                  tableRef.current &&
                    doc.html(tableRef.current, {
                      callback: (doc) => {
                        const pageWidth = doc.internal.pageSize.getWidth()
                        const pageHeight = doc.internal.pageSize.getHeight()

                        const header = `${company.label} | ${financialYear.value}`
                        doc.setFontSize(26)
                        doc.setFont('Arial', 'italic', 600)
                        doc.text(header, (pageWidth - 300) / 2, 30)

                        const footer = `${new Date().getFullYear().toString()}© String Labs`
                        doc.setFontSize(20)
                        doc.text(footer, 30, pageHeight - 20 - 10)
                        const totalPages = doc.getNumberOfPages()

                        doc.deletePage(totalPages - 1)
                        doc.deletePage(totalPages - 2)
                        doc.deletePage(totalPages - 4)
                        doc.deletePage(totalPages - 3)

                        doc.save(`Aging Report - ${company.label}(${financialYear.value})`)
                      },
                      margin: [80, 30, 30, 30],
                      autoPaging: 'text',
                    })
                }}
              >
                <span className='px-5'>Download</span>
                <span
                  style={{
                    color: '#fff',
                    backgroundColor: '#006060',
                    padding: '0 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    borderTopRightRadius: 'inherit',
                    borderBottomRightRadius: 'inherit',
                  }}
                >
                  <KTIcon iconName='arrow-down' className='fs-2 text-white' />
                </span>
              </button>
              {/* end::Add user */}
            </div>

            {/* end::Group actions */}
          </div>
          {/* end::Card toolbar */}
        </div>
      </KTCard>
      <KTCard>
        <KTCardBody className='py-4'>
          {!isLoading && (
            <div className='table-responsive'>
              <table
                ref={tableRef}
                id='aging_report_table'
                className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                style={{
                  borderCollapse: 'collapse', // Changed from 'separate' to 'collapse'
                  borderSpacing: 0,
                  tableLayout: 'fixed', // Ensures consistent column widths
                }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'
                    >
                      {headerGroup.headers.map((header) => (
                        <CustomHeaderColumn
                          key={header.id}
                          header={header}
                          style={getCommonPinningStyles(header.column)}
                        />
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody style={{fontSize: '11px', fontWeight: '500', color: '#343541'}}>
                  {table.getRowModel().rows.length > 0 ? (
                    table
                      .getRowModel()
                      .rows.map((row, i) => (
                        <CustomRow
                          row={row}
                          key={`row-${i}-${row.id}`}
                          getCommonPinningStyles={getCommonPinningStyles}
                        />
                      ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                          No matching records found
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {isLoading && <Loader isError={isError} />}
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default AgingReport

// Sticky column pinning styles - similar to the TanStack example
const getCommonPinningStyles = (column: Column<AgingReportData>): React.CSSProperties => {
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right')

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-4px 0 4px -4px rgba(0, 0, 0, 0) inset'
      : isFirstRightPinnedColumn
      ? '4px 0 4px -4px rgba(0, 0, 0, 0) inset'
      : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    backgroundColor: 'inherit', // Inherit background from parent

    borderSpacing: 0,
    borderCollapse: 'collapse',
  }
}

// Tanstack types
// removed duplicate type imports

type CustomHeaderColumnProps = {
  header: TanstackHeader<AgingReportData, unknown>
  style?: React.CSSProperties
}

export const CustomHeaderColumn: React.FC<CustomHeaderColumnProps> = ({header, style}) => (
  <th
    style={style}
    className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'
  >
    {flexRender(header.column.columnDef.header, header.getContext())}
  </th>
)

type CustomRowProps = {
  row: TanstackRow<AgingReportData>
  getCommonPinningStyles: (column: Column<AgingReportData>) => React.CSSProperties
}

export const CustomRow: React.FC<CustomRowProps> = ({row, getCommonPinningStyles}) => {
  return (
    <tr className='bg-hover-light' style={{backgroundColor: '#fff'}}>
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className={clsx({'text-end min-w-100px': cell.column.id === 'actions'})}
          style={{
            ...getCommonPinningStyles(cell.column),
            backgroundColor: 'inherit', // This ensures the cell inherits the row background
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  )
}

export const styles = {
  borderRadius: '0.475rem',
  boxShadow: '0 0 50px 0 rgb(82 63 105 / 15%)',
  backgroundColor: '#fff',
  color: '#7e8299',
  fontWeight: '500',
  margin: '0',
  width: 'auto',
  padding: '1rem 2rem',
  top: 'calc(50% - 2rem)',
  left: 'calc(50% - 4rem)',
}
